import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { graphql } from '../gql';
import { IQueryGetEntitiesFieldArgs } from './types';
import {
  getFirstPaginatedNode,
  getPaginatedNodes,
  toPagination,
} from './utils';

export const GET_RISK_REPORT = graphql(`
  query getRiskReport($pagination: PaginationRequest, $uuid: String) {
    entities(
      filters: { submissionUuid: $uuid, submissionType: INSURED }
      pagination: $pagination
    ) {
      pageInfo {
        totalResults
        pageNumber
        pageSize
        hasNextPage
        hasPreviousPage
      }
      edges {
        cursor
        node {
          aliases(filters: { groupSources: true }) {
            edges {
              cursor
              node {
                uuid
                name
              }
            }
          }
          coverages(sort: { key: "order", order: "ASC" }) {
            edges {
              cursor
              node {
                uuid
                name
                order
                score
                minScore
                maxScore
                createdAt
              }
            }
          }
        }
      }
    }
  }
`);

export function useRiskReportQuery({
  submissionUuid,
}: Pick<IQueryGetEntitiesFieldArgs, 'submissionUuid'>) {
  const { loading, error, data } = useQuery(GET_RISK_REPORT, {
    variables: {
      uuid: submissionUuid,
      pagination: toPagination(0, 1),
    },
  });

  const info = useMemo(() => {
    const entity = getFirstPaginatedNode(data?.entities);
    const alias = getFirstPaginatedNode(entity?.aliases);
    const insuredName = alias?.name;
    const coverages = getPaginatedNodes(entity?.coverages);
    const riskReportCoverages = coverages.map((coverage) => {
      return {
        name: coverage.name ?? 'N/A',
        score: coverage.score ?? 0,
        minScore: coverage.minScore ?? 0,
        maxScore: coverage.maxScore ?? 100,
      };
    });
    return { riskReportCoverages, entity, alias, insuredName, coverages };
  }, [data]);

  return {
    error,
    loading,
    data,
    info,
  };
}
